import { getDbSupabase } from './supabase';

export const PRODUCT_IMAGES_BUCKET = 'product-images';

const SIGNED_URL_TTL_SEC = 60 * 60;
const CACHE_TTL_MS = 50 * 60 * 1000;

type CacheEntry = { url: string; expiresAt: number };

const signedUrlCache = new Map<string, CacheEntry>();

/** Extract `items/{code}.jpg` from stored public URL or raw path. */
export function parseItemImageStoragePath(imageUrl: string): string | null {
  const trimmed = imageUrl.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('items/')) {
    return trimmed.split('?')[0];
  }

  const match = trimmed.match(/product-images\/(items\/[^?]+)/i);
  if (match?.[1]) {
    return decodeURIComponent(match[1]);
  }

  return null;
}

export function getCachedDemItemImageUrl(imageUrl: string): string | null {
  const key = imageUrl.trim();
  if (!key) return null;

  const entry = signedUrlCache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    signedUrlCache.delete(key);
    return null;
  }
  return entry.url;
}

export function setCachedDemItemImageUrl(imageUrl: string, resolvedUrl: string): void {
  const key = imageUrl.trim();
  if (!key || !resolvedUrl) return;
  signedUrlCache.set(key, { url: resolvedUrl, expiresAt: Date.now() + CACHE_TTL_MS });
}

export function invalidateDemItemImageCache(imageUrl?: string | null): void {
  if (!imageUrl?.trim()) {
    signedUrlCache.clear();
    return;
  }
  signedUrlCache.delete(imageUrl.trim());
}

/** Resolve stored image_url to a browser-loadable URL (signed for private bucket). */
export async function resolveDemItemImageUrl(imageUrl: string): Promise<string | null> {
  const trimmed = imageUrl.trim();
  if (!trimmed) return null;

  const cached = getCachedDemItemImageUrl(trimmed);
  if (cached) return cached;

  const storagePath = parseItemImageStoragePath(trimmed);
  if (!storagePath) {
    // Non-Supabase URL — use as-is.
    return trimmed;
  }

  const bucket = getDbSupabase('dem').storage.from(PRODUCT_IMAGES_BUCKET);
  const { data, error } = await bucket.createSignedUrl(storagePath, SIGNED_URL_TTL_SEC);
  if (!error && data?.signedUrl) {
    setCachedDemItemImageUrl(trimmed, data.signedUrl);
    return data.signedUrl;
  }

  const { data: pub } = bucket.getPublicUrl(storagePath);
  if (pub.publicUrl) {
    setCachedDemItemImageUrl(trimmed, pub.publicUrl);
    return pub.publicUrl;
  }

  return null;
}
